import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { buildOrgUrl, ApiResponseError } from '@app/utils/api';
import { browserTimeZone } from '@app/utils/chatTimeContext';
import { ChatInteractContextschemaToJSON } from '@app/types/sdk';
import { streamSse } from '@app/utils/sse';
import { appendRunActivity, buildRunActivityEntry, summarizeRunEvent, type RunActivityEntry, type RunTraceLevel, RUN_TRACE_LEVEL_OPTIONS } from '@app/utils/chatRunTrace';
import type { ArtifactDocumentschema } from '@reap/openapi-public-v1';
import {
  ChatMessageContentBlocks,
  getPromptKey,
  getPromptMessageId,
  getPromptAnswerState,
  resolveChatMessageText,
  type ChatChoiceOptionRecord,
  type ChatChoicePromptBlock,
  type ChatFormFieldRecord,
  type ChatFormPromptBlock,
  type ChatInputPromptBlock,
  type ChatInteractRequestContentBlock,
  type ChatMessageContentBlock,
  type ChatMessageContentEvent,
  type ChatConfirmationPromptBlock,
  type PromptChoiceDraftMap,
  type PromptFormDraftMap,
  type PromptInputDraftMap,
  type PromptSubmissionStateMap
} from '@app/components/chat/ChatMessageContentBlocks';
import {
  formatArtifactSize,
  isFileArtifactKind,
  mapAttachmentsToArtifacts,
  type ChatAttachmentArtifact,
  type ChatMessageAttachmentRecord
} from '@app/components/chat/chatAttachments';
import ChatFilePreviewPane from '@app/components/chat/ChatFilePreviewPane';
import ComposerAttachmentPicker from '@app/components/chat/ComposerAttachmentPicker';
import {
  buildFileDownloadPath,
  buildFileMetadataPath,
  buildFileTextContentPath,
  deriveFileDownloadName,
  downloadBlobAsFile,
  formatFilePreviewErrorMessage,
  hasMoreFileText,
  inferReadableUploadMime,
  loadFileArtifactPreview,
  parseArtifactDocument,
  parseFileTextSlice,
  parseUploadedFileRecord,
  resolveArtifactPathWithContent,
  type ChatArtifactLike,
  type FileTextSliceRecord,
  type UploadedFileRecord
} from '@app/components/chat/chatArtifactUtils';
import {
  createAndDownloadOutputExport,
  OUTPUT_EXPORT_FORMAT_LABELS,
  resolveArtifactExportFormats,
  resolveDefaultArtifactExportFormat,
  type OutputExportFormat,
} from '@app/components/chat/outputExportTransport';
import { mergeChatThreadTags, TOPOLOGY_WORKBENCH_THREAD_TAG } from '@app/utils/chatThreadTags';
import {
  buildTopologyWorkbenchChatHistoryBoundary,
  filterTopologyWorkbenchChatHistory,
  readTopologyWorkbenchChatHistoryBoundary,
  writeTopologyWorkbenchChatHistoryBoundary,
  type TopologyWorkbenchChatHistoryBoundary
} from './topologyWorkbenchChatHistory';
import type { ChatInteractContextschema, VisibilitySession } from '@app/types/sdk';

interface ChatMessageRecord {
  id?: string;
  role?: string;
  createdAt?: string;
  runId?: string;
  content?: ChatMessageContentBlock[];
  attachments?: ChatMessageAttachmentRecord[];
}

interface CursorListResponse<T> {
  items?: T[];
  meta?: {
    next?: string | null;
  };
}

interface ChatThreadRecord {
  id?: string;
  tags?: string[];
}

interface PendingComposerFile {
  localId: string;
  source: 'local' | 'promoted';
  file?: File;
  fileRecord?: UploadedFileRecord;
  status: 'pending' | 'uploading' | 'uploaded';
  uploadedAttachment?: Record<string, unknown>;
}

interface ArtifactPreviewState {
  loading: boolean;
  content: string;
  error?: string;
  isFull?: boolean;
  objectUrl?: string;
  doc?: ArtifactDocumentschema | null;
  fileText?: FileTextSliceRecord;
  fileRecord?: UploadedFileRecord;
}

interface ChatTimelineEvent extends ChatMessageContentEvent {
  role: 'assistant' | 'user';
  timestamp: string;
  attachments?: ChatMessageAttachmentRecord[];
  attachmentArtifacts?: ChatAttachmentArtifact[];
}

type RequestOrgApi = (resource: string, init?: RequestInit) => Promise<Response>;

interface TopologyWorkbenchChatPanelProps {
  orgId: string;
  basePath: string;
  threadId: string | null;
  session: VisibilitySession | null;
  requestOrgApi: RequestOrgApi;
  ensureSession: (threadId: string) => Promise<VisibilitySession>;
  buildInteractContext: (session: VisibilitySession) => ChatInteractContextschema;
  onThreadIdChange: (threadId: string | null) => void;
  onSessionRefreshRequested?: () => void | Promise<void>;
  disabled?: boolean;
  disabledReason?: string | null;
  disabledActions?: ReactNode;
  title?: string;
  subtitle?: string | null;
}

const createClientRequestId = () =>
  `ui-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const extractRunIdFromLocation = (location: string | null): string | null => {
  if (!location) {
    return null;
  }
  const match = location.match(/chat\/runs\/([^/?]+)/i);
  return match ? match[1] ?? null : null;
};

const formatProblem = async (error: unknown, fallback: string): Promise<string> => {
  if (!(error instanceof ApiResponseError)) {
    return error instanceof Error && error.message ? error.message : fallback;
  }
  if (error.response.status === 409) {
    return 'Another request is still active for this chat thread. Refreshing the thread state; try again after the active request finishes or clears.';
  }
  try {
    const payload = (await error.response.clone().json()) as { title?: string; detail?: string; message?: string };
    return payload.detail || payload.message || payload.title || `${fallback} (status ${error.response.status})`;
  } catch {
    return `${fallback} (status ${error.response.status})`;
  }
};

const isConflictError = (error: unknown): boolean =>
  error instanceof ApiResponseError && error.response.status === 409;

const sortMessages = (messages: ChatMessageRecord[]): ChatMessageRecord[] =>
  [...messages].sort((left, right) => {
    const leftTime = left.createdAt ? Date.parse(left.createdAt) : 0;
    const rightTime = right.createdAt ? Date.parse(right.createdAt) : 0;
    return leftTime - rightTime;
  });

const isTerminalRunStatus = (value: unknown): boolean => {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase() : '';
  return (
    normalized === 'succeeded' ||
    normalized === 'failed' ||
    normalized === 'cancelled' ||
    normalized === 'canceled' ||
    normalized === 'rejected' ||
    normalized === 'completed'
  );
};

const revokeArtifactObjectUrl = (objectUrls: Record<string, string>, artifactId: string) => {
  const objectUrl = objectUrls[artifactId];
  if (!objectUrl) {
    return;
  }
  URL.revokeObjectURL(objectUrl);
  delete objectUrls[artifactId];
};

const resolveArtifactDocumentText = (doc: ArtifactDocumentschema, full: boolean): string => {
  const anyDoc = doc as any;
  const view = Array.isArray(anyDoc.views) && anyDoc.views.length > 0 ? anyDoc.views[0] : undefined;
  const content = view?.content ?? anyDoc.content;

  if (content?.type === 'commandOutput') {
    const fullText = typeof content.fullText === 'string' ? content.fullText : '';
    const previewText = typeof content.previewText === 'string' ? content.previewText : '';
    return full && fullText ? fullText : previewText || fullText || '';
  }

  if (content?.type === 'table') {
    const rows = Array.isArray(content.rows) ? content.rows : [];
    return JSON.stringify(rows, null, 2);
  }

  return JSON.stringify(content ?? anyDoc, null, 2);
};

const renderArtifactPreview = (
  artifact: ChatAttachmentArtifact,
  preview: ArtifactPreviewState,
  onLoadMore: () => void,
  onDownload: (artifact: ChatAttachmentArtifact) => void
) => {
  if (isFileArtifactKind(artifact.kind)) {
    return (
      <ChatFilePreviewPane
        artifactId={artifact.id}
        file={preview.fileRecord}
        mimeType={artifact.mimeType}
        sizeBytes={artifact.size}
        text={preview.content}
        fileText={preview.fileText}
        objectUrl={preview.objectUrl}
        loading={preview.loading}
        onDownload={() => onDownload(artifact)}
        onLoadMore={onLoadMore}
        renderPreview={(artifactId, content, options) => (
          <>
            {(preview.fileText?.lines ?? content.split('\n').map((text, index) => ({ lineNo: index + 1, text }))).map((line) => (
              <span
                key={`${artifactId}-line-${line.lineNo}`}
                className={options?.showLineNumbers ? 'chat-artifact-line chat-artifact-line--numbered' : 'chat-artifact-line'}
                data-line={line.lineNo}
              >
                {line.text || ' '}
              </span>
            ))}
          </>
        )}
      />
    );
  }

  const mime = (artifact.mimeType ?? '').toLowerCase();
  if (mime.startsWith('image/') && preview.objectUrl) {
    return <img className="topology-workbench__chat-preview-image" src={preview.objectUrl} alt={artifact.summary ?? 'Artifact preview'} />;
  }
  if (mime === 'application/pdf' && preview.objectUrl) {
    return <iframe className="topology-workbench__chat-preview-frame" src={preview.objectUrl} title={artifact.summary ?? 'Artifact preview'} />;
  }
  return <pre className="topology-workbench__chat-preview-body">{preview.content || 'No preview available.'}</pre>;
};

const TopologyWorkbenchChatPanel = ({
  orgId,
  basePath,
  threadId,
  session,
  requestOrgApi,
  ensureSession,
  buildInteractContext,
  onThreadIdChange,
  onSessionRefreshRequested,
  disabled = false,
  disabledReason,
  disabledActions,
  title = 'Contextual chat',
  subtitle
}: TopologyWorkbenchChatPanelProps) => {
  const [messages, setMessages] = useState<ChatMessageRecord[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState<string | null>(null);
  const [composerText, setComposerText] = useState('');
  const [composerError, setComposerError] = useState<string | null>(null);
  const [pendingFiles, setPendingFiles] = useState<PendingComposerFile[]>([]);
  const [traceLevel, setTraceLevel] = useState<RunTraceLevel>('summary');
  const [streamingRunId, setStreamingRunId] = useState<string | null>(null);
  const [runStatusText, setRunStatusText] = useState<string | null>(null);
  const [runActivityTrail, setRunActivityTrail] = useState<RunActivityEntry[]>([]);
  const [sending, setSending] = useState(false);
  const [promptChoiceDrafts, setPromptChoiceDrafts] = useState<PromptChoiceDraftMap>({});
  const [promptInputDrafts, setPromptInputDrafts] = useState<PromptInputDraftMap>({});
  const [promptFormDrafts, setPromptFormDrafts] = useState<PromptFormDraftMap>({});
  const [promptSubmissionState, setPromptSubmissionState] = useState<PromptSubmissionStateMap>({});
  const [historyBoundary, setHistoryBoundary] = useState<TopologyWorkbenchChatHistoryBoundary | null>(null);
  const [historyBoundaryActive, setHistoryBoundaryActive] = useState(false);
  const [showPreviousHistory, setShowPreviousHistory] = useState(false);
  const [artifactPreview, setArtifactPreview] = useState<Record<string, ArtifactPreviewState>>({});
  const [artifactExportFormats, setArtifactExportFormats] = useState<Record<string, OutputExportFormat>>({});
  const [selectedArtifact, setSelectedArtifact] = useState<ChatAttachmentArtifact | null>(null);

  const composerRef = useRef<HTMLTextAreaElement | null>(null);
  const streamControllerRef = useRef<AbortController | null>(null);
  const timelineRef = useRef<HTMLDivElement | null>(null);
  const taggedThreadIdsRef = useRef(new Set<string>());
  const historyBoundaryDecisionRef = useRef(false);
  const artifactObjectUrlsRef = useRef<Record<string, string>>({});

  const loadThreadMessages = useCallback(
    async (nextThreadId: string) => {
      const params = new URLSearchParams();
      params.set('limit', '50');
      params.set('order', 'desc');
      setMessagesLoading(true);
      setMessagesError(null);
      try {
        const response = await requestOrgApi(`chat/threads/${nextThreadId}/messages?${params.toString()}`, {
          method: 'GET'
        });
        const payload = (await response.json()) as CursorListResponse<ChatMessageRecord>;
        setMessages(sortMessages(payload.items ?? []));
      } catch (error) {
        setMessages([]);
        setMessagesError(await formatProblem(error, 'Failed to load messages'));
      } finally {
        setMessagesLoading(false);
      }
    },
    [requestOrgApi]
  );

  useEffect(() => {
    if (!threadId) {
      setMessages([]);
      setMessagesError(null);
      setSelectedArtifact(null);
      setArtifactPreview({});
      return;
    }
    void loadThreadMessages(threadId);
  }, [loadThreadMessages, threadId]);

  useEffect(() => {
    return () => {
      Object.keys(artifactObjectUrlsRef.current).forEach((artifactId) => {
        revokeArtifactObjectUrl(artifactObjectUrlsRef.current, artifactId);
      });
    };
  }, []);

  useEffect(() => {
    historyBoundaryDecisionRef.current = false;
    setShowPreviousHistory(false);
    if (!threadId) {
      setHistoryBoundary(null);
      setHistoryBoundaryActive(false);
      return;
    }
    setHistoryBoundary(readTopologyWorkbenchChatHistoryBoundary(orgId, threadId));
    setHistoryBoundaryActive(false);
  }, [orgId, threadId]);

  const isStableSession = useMemo(
    () => Boolean(session && !session.pendingProposalId && session.mode !== 'preview' && !streamingRunId && !sending),
    [sending, session, streamingRunId]
  );

  useEffect(() => {
    if (historyBoundaryDecisionRef.current) {
      return;
    }
    if (!threadId || !session || streamingRunId || sending) {
      return;
    }
    setHistoryBoundaryActive(Boolean(isStableSession && historyBoundary));
    historyBoundaryDecisionRef.current = true;
  }, [historyBoundary, isStableSession, sending, session, streamingRunId, threadId]);

  useEffect(() => {
    if (!threadId || !isStableSession || messagesLoading || messages.length === 0) {
      return;
    }
    writeTopologyWorkbenchChatHistoryBoundary(orgId, threadId, buildTopologyWorkbenchChatHistoryBoundary(messages));
  }, [isStableSession, messages, messages.length, messagesLoading, orgId, threadId]);

  const { visibleMessages, hiddenHistoryCount } = useMemo(() => {
    if (showPreviousHistory || !historyBoundaryActive) {
      return {
        visibleMessages: messages,
        hiddenHistoryCount: 0
      };
    }
    const filtered = filterTopologyWorkbenchChatHistory(messages, historyBoundary);
    return {
      visibleMessages: filtered.visibleMessages,
      hiddenHistoryCount: filtered.hiddenCount
    };
  }, [historyBoundary, historyBoundaryActive, messages, showPreviousHistory]);

  const messageEvents = useMemo<ChatTimelineEvent[]>(
    () =>
      visibleMessages.map((message) => {
        const role = message.role === 'assistant' ? 'assistant' : 'user';
        return {
          id: message.id ?? `${role}-${message.createdAt ?? Date.now()}`,
          messageId: message.id,
          role,
          type: 'message',
          text: resolveChatMessageText(message),
          contentBlocks: message.content ?? undefined,
          timestamp: message.createdAt ?? new Date().toISOString(),
          attachments: message.attachments ?? undefined,
          attachmentArtifacts: mapAttachmentsToArtifacts(message)
        };
      }),
    [visibleMessages]
  );

  const promptAnswerState = useMemo(() => getPromptAnswerState(messageEvents), [messageEvents]);
  const selectedArtifactPreview = selectedArtifact ? artifactPreview[selectedArtifact.id] : undefined;
  const selectedArtifactIsFile = isFileArtifactKind(selectedArtifact?.kind);

  useEffect(() => {
    if (!timelineRef.current) {
      return;
    }
    timelineRef.current.scrollTop = timelineRef.current.scrollHeight;
  }, [hiddenHistoryCount, messageEvents, runStatusText, showPreviousHistory]);

  const clearPendingFiles = useCallback(() => {
    setPendingFiles([]);
  }, []);

  const createThread = useCallback(async (): Promise<string> => {
    const response = await requestOrgApi('chat/threads', {
      method: 'POST',
      body: JSON.stringify({
        title: 'Topology workbench',
        visibility: 'private',
        tags: [TOPOLOGY_WORKBENCH_THREAD_TAG]
      })
    });
    const payload = (await response.json()) as { id?: string };
    const nextThreadId = typeof payload.id === 'string' ? payload.id.trim() : '';
    if (!nextThreadId) {
      throw new Error('Thread creation did not return an id.');
    }
    taggedThreadIdsRef.current.add(nextThreadId);
    onThreadIdChange(nextThreadId);
    return nextThreadId;
  }, [onThreadIdChange, requestOrgApi]);

  const ensureWorkbenchThreadTag = useCallback(
    async (nextThreadId: string) => {
      if (taggedThreadIdsRef.current.has(nextThreadId)) {
        return;
      }
      const response = await requestOrgApi(`chat/threads/${nextThreadId}`, { method: 'GET' });
      const payload = (await response.json()) as ChatThreadRecord;
      const mergedTags = mergeChatThreadTags(payload.tags, [TOPOLOGY_WORKBENCH_THREAD_TAG]);
      if (mergedTags.length !== (payload.tags ?? []).length) {
        await requestOrgApi(`chat/threads/${nextThreadId}`, {
          method: 'PATCH',
          body: JSON.stringify({
            tags: mergedTags
          })
        });
      }
      taggedThreadIdsRef.current.add(nextThreadId);
    },
    [requestOrgApi]
  );

  useEffect(() => {
    if (!threadId) {
      return;
    }
    void ensureWorkbenchThreadTag(threadId).catch((error) => {
      console.error('Failed to tag topology workbench thread', error);
    });
  }, [ensureWorkbenchThreadTag, threadId]);

  const requestSessionRefresh = useCallback(() => {
    void onSessionRefreshRequested?.();
  }, [onSessionRefreshRequested]);

  const resolveArtifactPath = useCallback((artifact: ChatArtifactLike): string | null => {
    if (!artifact.id) {
      return null;
    }
    if (isFileArtifactKind(artifact.kind)) {
      return `files/${artifact.id}`;
    }
    return `chat/artifacts/${artifact.id}`;
  }, []);

  const downloadFileBytes = useCallback(
    async (fileId: string, accept: string) => {
      const response = await requestOrgApi(buildFileDownloadPath(fileId), {
        headers: { Accept: accept }
      });
      return response.blob();
    },
    [requestOrgApi]
  );

  const loadFileArtifactState = useCallback(
    async (artifactId: string, existingPreview: ArtifactPreviewState | undefined, full = false) =>
      loadFileArtifactPreview({
        fileId: artifactId,
        full,
        existing: {
          fileRecord: existingPreview?.fileRecord,
          fileText: full ? existingPreview?.fileText : undefined
        },
        loadMetadata: async (fileId) => {
          const metadataResponse = await requestOrgApi(buildFileMetadataPath(fileId), {
            headers: { Accept: 'application/json' }
          });
          return parseUploadedFileRecord(await metadataResponse.json());
        },
        loadTextSlice: async (fileId, startLine, maxLines) => {
          const response = await requestOrgApi(buildFileTextContentPath(fileId, startLine, maxLines), {
            headers: { Accept: 'application/json' }
          });
          return parseFileTextSlice(await response.json());
        },
        loadDownloadBlob: downloadFileBytes
      }),
    [downloadFileBytes, requestOrgApi]
  );

  const loadArtifactPreview = useCallback(
    async (artifact: ChatAttachmentArtifact, full = false) => {
      if (!artifact.id) {
        return;
      }
      setArtifactPreview((prev) => ({
        ...prev,
        [artifact.id]: {
          ...(prev[artifact.id] ?? { content: '' }),
          loading: true,
          error: undefined
        }
      }));
      try {
        if (isFileArtifactKind(artifact.kind)) {
          const existingPreview = artifactPreview[artifact.id];
          const loadedPreview = await loadFileArtifactState(artifact.id, existingPreview, full);
          setArtifactPreview((prev) => {
            revokeArtifactObjectUrl(artifactObjectUrlsRef.current, artifact.id);
            if (loadedPreview.objectUrl) {
              artifactObjectUrlsRef.current[artifact.id] = loadedPreview.objectUrl;
            }
            return {
              ...prev,
              [artifact.id]: {
                loading: false,
                content: loadedPreview.content,
                fileText: loadedPreview.fileText,
                fileRecord: loadedPreview.fileRecord,
                objectUrl: loadedPreview.objectUrl,
                isFull: loadedPreview.isFull
              }
            };
          });
          return;
        }

        const path = resolveArtifactPathWithContent(resolveArtifactPath, artifact, full);
        if (!path) {
          return;
        }

        const mime = (artifact.mimeType ?? '').toLowerCase();
        if (mime.startsWith('image/') || mime === 'application/pdf') {
          const response = await requestOrgApi(path, {
            headers: { Accept: mime || '*/*' }
          });
          const blob = await response.blob();
          const objectUrl = URL.createObjectURL(blob);
          setArtifactPreview((prev) => {
            const current = prev[artifact.id];
            if (current?.objectUrl && current.objectUrl !== objectUrl) {
              revokeArtifactObjectUrl(artifactObjectUrlsRef.current, artifact.id);
            }
            artifactObjectUrlsRef.current[artifact.id] = objectUrl;
            return {
              ...prev,
              [artifact.id]: {
                loading: false,
                content: objectUrl,
                objectUrl,
                isFull: true
              }
            };
          });
          return;
        }

        const response = await requestOrgApi(path, {
          headers: { Accept: 'application/vnd.reap.artifact+json' }
        });
        const raw = await response.text();
        const doc = parseArtifactDocument(raw);
        const content = doc ? resolveArtifactDocumentText(doc, full) : raw;
        setArtifactPreview((prev) => {
          revokeArtifactObjectUrl(artifactObjectUrlsRef.current, artifact.id);
          return {
            ...prev,
            [artifact.id]: {
              loading: false,
              content,
              doc,
              isFull: full
            }
          };
        });
      } catch (error) {
        setArtifactPreview((prev) => {
          revokeArtifactObjectUrl(artifactObjectUrlsRef.current, artifact.id);
          return {
            ...prev,
            [artifact.id]: {
              ...(prev[artifact.id] ?? { content: '' }),
              loading: false,
              error: isFileArtifactKind(artifact.kind) ? formatFilePreviewErrorMessage(error) : error instanceof Error ? error.message : 'Failed to load artifact'
            }
          };
        });
      }
    },
    [artifactPreview, loadFileArtifactState, requestOrgApi, resolveArtifactPath]
  );

  const handleSelectArtifact = useCallback(
    async (artifact: ChatAttachmentArtifact) => {
      if (selectedArtifact?.id === artifact.id) {
        setSelectedArtifact(null);
        return;
      }
      setSelectedArtifact(artifact);
      const existing = artifactPreview[artifact.id];
      if (!existing || existing.error) {
        await loadArtifactPreview(artifact, false);
      }
    },
    [artifactPreview, loadArtifactPreview, selectedArtifact?.id]
  );

  const handleOpenArtifactInNewTab = useCallback(
    async (artifact: ChatAttachmentArtifact) => {
      if (isFileArtifactKind(artifact.kind)) {
        return;
      }
      const path = resolveArtifactPathWithContent(resolveArtifactPath, artifact, true);
      if (!path) {
        return;
      }
      const url = buildOrgUrl(basePath, orgId, path);
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    [basePath, orgId, resolveArtifactPath]
  );

  const handleDownloadFile = useCallback(
    async (artifact: ChatAttachmentArtifact, explicitFormat?: OutputExportFormat) => {
      if (!artifact.id) {
        return;
      }
      try {
        if (isFileArtifactKind(artifact.kind)) {
          const blob = await downloadFileBytes(artifact.id, artifact.mimeType ?? 'application/octet-stream');
          downloadBlobAsFile(
            blob,
            deriveFileDownloadName(artifact.id, artifactPreview[artifact.id]?.fileRecord, artifact.summary)
          );
          return;
        }
        const format = explicitFormat ?? artifactExportFormats[artifact.id] ?? resolveDefaultArtifactExportFormat(artifact);
        const fallbackStem =
          (typeof artifact.summary === 'string' && artifact.summary.trim().length > 0
            ? artifact.summary
            : `${artifact.kind ?? 'artifact'}-${artifact.id}`);
        const downloaded = await createAndDownloadOutputExport(
          requestOrgApi,
          {
            resourceRefs: [`artifact:${artifact.id}`],
            purpose: 'ui_download',
            bundleMode: 'single',
            ...(format ? { format } : {}),
          },
          fallbackStem,
        );
        downloadBlobAsFile(downloaded.blob, downloaded.fileName);
      } catch (error) {
        setArtifactPreview((prev) => ({
          ...prev,
          [artifact.id]: {
            ...(prev[artifact.id] ?? { content: '' }),
            loading: false,
            error: isFileArtifactKind(artifact.kind)
              ? formatFilePreviewErrorMessage(error)
              : error instanceof Error && error.message
                ? error.message
                : 'Failed to download artifact',
          }
        }));
      }
    },
    [artifactExportFormats, artifactPreview, downloadFileBytes, requestOrgApi]
  );

  const renderArtifactDownloadAction = useCallback(
    (artifact: ChatAttachmentArtifact, buttonClassName: string, selectClassName?: string) => {
      if (isFileArtifactKind(artifact.kind)) {
        return null;
      }
      const formats = resolveArtifactExportFormats(artifact);
      const selectedFormat = artifactExportFormats[artifact.id] ?? resolveDefaultArtifactExportFormat(artifact);
      return (
        <>
          {formats.length > 1 ? (
            <label className="chat-export-format">
              <span className="chat-export-format__label">Format</span>
              <select
                className={selectClassName ?? 'chat-export-format__select'}
                value={selectedFormat ?? formats[0]}
                onChange={(event) =>
                  setArtifactExportFormats((previous) => ({
                    ...previous,
                    [artifact.id]: event.target.value as OutputExportFormat,
                  }))
                }
              >
                {formats.map((format) => (
                  <option key={format} value={format}>
                    {OUTPUT_EXPORT_FORMAT_LABELS[format]}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <button
            type="button"
            className={buttonClassName}
            onClick={() => void handleDownloadFile(artifact, selectedFormat)}
          >
            Download
          </button>
        </>
      );
    },
    [artifactExportFormats, handleDownloadFile]
  );

  const uploadComposerFiles = useCallback(
    async (nextThreadId: string, files: PendingComposerFile[]) => {
      const uploaded: Array<Record<string, unknown>> = [];
      for (const item of files) {
        if (item.source === 'promoted') {
          if (item.uploadedAttachment) {
            uploaded.push(item.uploadedAttachment);
          }
          continue;
        }
        if (!item.file) {
          continue;
        }
        setPendingFiles((prev) =>
          prev.map((entry) => (entry.localId === item.localId ? { ...entry, status: 'uploading' } : entry))
        );
        const formData = new FormData();
        formData.append('file', item.file, item.file.name);
        formData.append('threadId', nextThreadId);
        formData.append('name', item.file.name);
        const response = await requestOrgApi('files', {
          method: 'POST',
          body: formData
        });
        const payload = parseUploadedFileRecord(await response.json());
        if (!payload) {
          throw new Error(`Upload response for ${item.file.name} did not match Fileschema.`);
        }
        const resourceId = typeof payload.id === 'string' ? payload.id.trim() : '';
        if (!resourceId) {
          throw new Error(`Upload response for ${item.file.name} did not include a file id.`);
        }
        uploaded.push({
          resourceType: 'file',
          resourceId,
          name: (payload.filename ?? item.file.name).trim() || item.file.name,
          mimeType: (payload.mimeType ?? inferReadableUploadMime(item.file)).trim() || undefined,
          sizeBytes: typeof payload.sizeBytes === 'number' ? payload.sizeBytes : item.file.size
        });
      }
      return uploaded;
    },
    [requestOrgApi]
  );

  const interactWithThread = useCallback(
    async (
      nextThreadId: string,
      options: {
        content?: ChatInteractRequestContentBlock[];
        attachments?: Array<Record<string, unknown>>;
      }
    ) => {
      const nextSession = await ensureSession(nextThreadId);
      const context = {
        ...buildInteractContext(nextSession),
        ...(browserTimeZone() ? { timeZone: browserTimeZone() } : {})
      };
      const response = await requestOrgApi(`chat/threads/${nextThreadId}/interact`, {
        method: 'POST',
        body: JSON.stringify({
          ...(options.content && options.content.length > 0 ? { content: options.content } : {}),
          ...(options.attachments && options.attachments.length > 0 ? { attachments: options.attachments } : {}),
          context: ChatInteractContextschemaToJSON(context),
          trace: { level: traceLevel }
        })
      });
      const payload = (await response.json()) as ChatMessageRecord;
      setMessages((prev) => sortMessages([...prev, payload]));
      const runId = extractRunIdFromLocation(response.headers.get('Location'));
      if (runId) {
        setStreamingRunId(runId);
        setRunStatusText('Working…');
        setRunActivityTrail([]);
      } else {
        setRunStatusText(null);
        setRunActivityTrail([]);
        void loadThreadMessages(nextThreadId);
        requestSessionRefresh();
      }
      return payload;
    },
    [buildInteractContext, ensureSession, loadThreadMessages, requestOrgApi, requestSessionRefresh, traceLevel]
  );

  const submitPromptBlocks = useCallback(
    async (
      messageId: string | undefined,
      questionId: string | undefined,
      blockType: string | undefined,
      blockIndex: number,
      content: ChatInteractRequestContentBlock[]
    ) => {
      if (!threadId) {
        return;
      }
      const promptKey = getPromptKey(messageId, questionId, blockType, blockIndex);
      setPromptSubmissionState((prev) => ({
        ...prev,
        [promptKey]: { pending: true }
      }));
      try {
        await interactWithThread(threadId, { content });
        setPromptSubmissionState((prev) => ({
          ...prev,
          [promptKey]: { pending: false }
        }));
      } catch (error) {
        const message = await formatProblem(error, 'Failed to send reply');
        if (isConflictError(error)) {
          void loadThreadMessages(threadId);
          requestSessionRefresh();
        }
        setPromptSubmissionState((prev) => ({
          ...prev,
          [promptKey]: { pending: false, error: message }
        }));
      }
    },
    [interactWithThread, loadThreadMessages, requestSessionRefresh, threadId]
  );

  const submitSingleChoicePrompt = useCallback(
    async (event: ChatMessageContentEvent, block: ChatChoicePromptBlock, blockIndex: number, option: ChatChoiceOptionRecord) => {
      await submitPromptBlocks(getPromptMessageId(event), block.questionId, block.type, blockIndex, [
        {
          type: 'choiceResponse',
          questionId: block.questionId,
          responseKind: 'choice',
          choiceId: option.id,
          label: option.label
        }
      ]);
    },
    [submitPromptBlocks]
  );

  const submitChoicePromptDraft = useCallback(
    async (event: ChatMessageContentEvent, block: ChatChoicePromptBlock, blockIndex: number) => {
      const promptKey = getPromptKey(getPromptMessageId(event), block.questionId, block.type, blockIndex);
      const draft = promptChoiceDrafts[promptKey] ?? { selectedIds: [], useFreeform: false, freeformText: '' };
      const selectionMode = block.selectionMode === 'multi' ? 'multi' : 'single';
      if (draft.useFreeform) {
        const text = draft.freeformText.trim();
        if (!text) {
          setPromptSubmissionState((prev) => ({
            ...prev,
            [promptKey]: { pending: false, error: 'A custom response is required.' }
          }));
          return;
        }
        await submitPromptBlocks(getPromptMessageId(event), block.questionId, block.type, blockIndex, [
          {
            type: 'choiceResponse',
            questionId: block.questionId,
            responseKind: 'freeform',
            text
          }
        ]);
        return;
      }

      const selectedIds = draft.selectedIds.filter(Boolean);
      const choices = Array.isArray(block.choices) ? block.choices : [];
      const selectedChoices = choices.filter((choice) => choice.id && selectedIds.includes(choice.id));
      const minSelections = typeof block.minSelections === 'number' ? block.minSelections : 1;
      const maxSelections =
        typeof block.maxSelections === 'number'
          ? block.maxSelections
          : selectionMode === 'single'
            ? 1
            : undefined;
      if (selectedChoices.length < minSelections) {
        setPromptSubmissionState((prev) => ({
          ...prev,
          [promptKey]: { pending: false, error: `Select at least ${minSelections} option${minSelections === 1 ? '' : 's'}.` }
        }));
        return;
      }
      if (typeof maxSelections === 'number' && selectedChoices.length > maxSelections) {
        setPromptSubmissionState((prev) => ({
          ...prev,
          [promptKey]: { pending: false, error: `Select no more than ${maxSelections} option${maxSelections === 1 ? '' : 's'}.` }
        }));
        return;
      }
      if (selectionMode === 'single' && selectedChoices.length === 1) {
        await submitPromptBlocks(getPromptMessageId(event), block.questionId, block.type, blockIndex, [
          {
            type: 'choiceResponse',
            questionId: block.questionId,
            responseKind: 'choice',
            choiceId: selectedChoices[0].id,
            label: selectedChoices[0].label
          }
        ]);
        return;
      }
      await submitPromptBlocks(getPromptMessageId(event), block.questionId, block.type, blockIndex, [
        {
          type: 'choiceResponse',
          questionId: block.questionId,
          responseKind: 'choice',
          choiceIds: selectedChoices.map((choice) => choice.id ?? ''),
          labels: selectedChoices.map((choice) => choice.label ?? choice.id ?? '')
        }
      ]);
    },
    [promptChoiceDrafts, submitPromptBlocks]
  );

  const submitConfirmationPrompt = useCallback(
    async (event: ChatMessageContentEvent, block: ChatConfirmationPromptBlock, blockIndex: number, confirmed: boolean) => {
      await submitPromptBlocks(getPromptMessageId(event), block.questionId, block.type, blockIndex, [
        {
          type: 'confirmationResponse',
          questionId: block.questionId,
          decision: confirmed ? 'confirm' : 'cancel'
        }
      ]);
    },
    [submitPromptBlocks]
  );

  const submitInputPrompt = useCallback(
    async (event: ChatMessageContentEvent, block: ChatInputPromptBlock, blockIndex: number) => {
      const promptKey = getPromptKey(getPromptMessageId(event), block.questionId, block.type, blockIndex);
      const rawValue = promptInputDrafts[promptKey] ?? '';
      const value = rawValue.trim();
      if (!value && !block.allowEmpty) {
        setPromptSubmissionState((prev) => ({
          ...prev,
          [promptKey]: { pending: false, error: 'A response is required.' }
        }));
        return;
      }
      await submitPromptBlocks(getPromptMessageId(event), block.questionId, block.type, blockIndex, [
        {
          type: 'inputResponse',
          questionId: block.questionId,
          text: value
        }
      ]);
      setPromptInputDrafts((prev) => ({
        ...prev,
        [promptKey]: ''
      }));
    },
    [promptInputDrafts, submitPromptBlocks]
  );

  const submitFormPrompt = useCallback(
    async (event: ChatMessageContentEvent, block: ChatFormPromptBlock, blockIndex: number) => {
      const promptKey = getPromptKey(getPromptMessageId(event), block.questionId, block.type, blockIndex);
      const draft = promptFormDrafts[promptKey] ?? {};
      const fields = Array.isArray(block.fields) ? (block.fields as ChatFormFieldRecord[]) : [];
      const missingField = fields.find((field) => field.required && !(draft[field.id ?? ''] ?? '').trim());
      if (missingField) {
        setPromptSubmissionState((prev) => ({
          ...prev,
          [promptKey]: { pending: false, error: `${missingField.label ?? 'A field'} is required.` }
        }));
        return;
      }
      await submitPromptBlocks(getPromptMessageId(event), block.questionId, block.type, blockIndex, [
        {
          type: 'formResponse',
          questionId: block.questionId,
          fields: fields.map((field) => ({
            fieldId: field.id,
            valueText: draft[field.id ?? ''] ?? ''
          }))
        }
      ]);
    },
    [promptFormDrafts, submitPromptBlocks]
  );

  useEffect(() => {
    const runId = streamingRunId;
    if (!runId || !threadId) {
      streamControllerRef.current?.abort();
      streamControllerRef.current = null;
      return;
    }

    const controller = new AbortController();
    streamControllerRef.current = controller;
    let cancelled = false;
    const streamUrl = new URL(buildOrgUrl(basePath, orgId, `chat/runs/${runId}/events`));

    void streamSse(
      streamUrl.toString(),
      {
        onEvent: (eventType, data, eventId) => {
          if (cancelled) {
            return;
          }
          let payload: Record<string, unknown> = {};
          try {
            payload = JSON.parse(data) as Record<string, unknown>;
          } catch {
            payload = {};
          }
          const summary = summarizeRunEvent(eventType, payload);
          if (summary) {
            setRunStatusText(summary);
          }
          const activity = buildRunActivityEntry(eventType, payload, eventId);
          if (activity) {
            setRunActivityTrail((prev) => appendRunActivity(prev, activity));
          }
          if (eventType === 'message') {
            void loadThreadMessages(threadId);
            requestSessionRefresh();
          }
          if (eventType === 'error' || (eventType === 'status' && isTerminalRunStatus(payload.status))) {
            setStreamingRunId(null);
            void loadThreadMessages(threadId);
            requestSessionRefresh();
          }
        },
        reconnect: {
          enabled: true,
          maxAttempts: 2,
          retryDelayMs: 1000
        }
      },
      controller.signal
    )
      .catch((error) => {
        if (cancelled || controller.signal.aborted) {
          return;
        }
        console.error('Failed to stream topology workbench chat run', error);
        setRunStatusText('Run stream disconnected');
      })
      .finally(() => {
        if (streamControllerRef.current === controller) {
          streamControllerRef.current = null;
        }
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [basePath, loadThreadMessages, orgId, requestSessionRefresh, streamingRunId, threadId]);

  const handleSelectFiles = useCallback((files: File[]) => {
    if (files.length === 0) {
      return;
    }
    setPendingFiles((prev) => [
      ...prev,
      ...files.map((file) => ({
        localId: createClientRequestId(),
        source: 'local' as const,
        file,
        status: 'pending' as const
      }))
    ]);
    setComposerError(null);
  }, []);

  const handleAttachPromotedFile = useCallback((fileRecord: UploadedFileRecord) => {
    const resourceId = (fileRecord.id ?? '').trim();
    if (!resourceId) {
      return;
    }
    setComposerError(null);
    setPendingFiles((previous) => {
      if (previous.some((item) => item.source === 'promoted' && item.uploadedAttachment?.resourceId === resourceId)) {
        return previous;
      }
      return [
        ...previous,
        {
          localId: createClientRequestId(),
          source: 'promoted',
          fileRecord,
          status: 'uploaded',
          uploadedAttachment: {
            resourceType: 'file',
            resourceId,
            name: (fileRecord.filename ?? fileRecord.name ?? resourceId).trim() || resourceId,
            mimeType: (fileRecord.mimeType ?? '').trim() || undefined,
            sizeBytes: typeof fileRecord.sizeBytes === 'number' ? fileRecord.sizeBytes : undefined,
          },
        },
      ];
    });
  }, []);

  const removePendingFile = useCallback((localId: string) => {
    setPendingFiles((prev) => prev.filter((item) => item.localId !== localId));
  }, []);

  const handleSendMessage = useCallback(async () => {
    const trimmed = composerText.trim();
    if (disabled || sending || (!trimmed && pendingFiles.length === 0)) {
      return;
    }
    setSending(true);
    setComposerError(null);
    setRunStatusText('Preparing request…');
    let nextThreadId = threadId;
    try {
      if (!nextThreadId) {
        nextThreadId = await createThread();
      }
      const attachments =
        pendingFiles.length > 0 ? await uploadComposerFiles(nextThreadId, [...pendingFiles]) : undefined;
      await interactWithThread(nextThreadId, {
        ...(trimmed ? { content: [{ type: 'text', text: trimmed } satisfies ChatInteractRequestContentBlock] } : {}),
        ...(attachments && attachments.length > 0 ? { attachments } : {})
      });
      setComposerText('');
      clearPendingFiles();
    } catch (error) {
      setPendingFiles((prev) =>
        prev.map((item) => (item.status === 'uploading' ? { ...item, status: 'pending' } : item))
      );
      setComposerError(await formatProblem(error, 'Failed to send message'));
      if (nextThreadId && isConflictError(error)) {
        void loadThreadMessages(nextThreadId);
        requestSessionRefresh();
      }
      setRunStatusText(null);
      setStreamingRunId(null);
    } finally {
      setSending(false);
    }
  }, [
    buildInteractContext,
    clearPendingFiles,
    composerText,
    createThread,
    disabled,
    interactWithThread,
    loadThreadMessages,
    pendingFiles,
    requestSessionRefresh,
    sending,
    threadId,
    uploadComposerFiles
  ]);

  const handleComposerKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key !== 'Enter') {
        return;
      }
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        const target = event.currentTarget;
        const start = target.selectionStart ?? target.value.length;
        const end = target.selectionEnd ?? target.value.length;
        const nextValue = `${target.value.slice(0, start)}\n${target.value.slice(end)}`;
        setComposerText(nextValue);
        requestAnimationFrame(() => {
          if (composerRef.current) {
            composerRef.current.selectionStart = composerRef.current.selectionEnd = start + 1;
          }
        });
        return;
      }
      event.preventDefault();
      void handleSendMessage();
    },
    [handleSendMessage]
  );

  const canSend = useMemo(
    () => !disabled && !sending && (composerText.trim().length > 0 || pendingFiles.length > 0),
    [composerText, disabled, pendingFiles.length, sending]
  );

  return (
    <section className="section-card topology-workbench__chat-card">
      <div className="topology-workbench__chat-header">
        <div>
          <h2 className="topology-workbench__chat-title">{title}</h2>
          <p className="topology-workbench__chat-subtitle">
            {subtitle ?? (session?.id ? 'Working against the current topology session.' : 'Starts on first send')}
          </p>
        </div>
        {streamingRunId && <span className="topology-workbench__chat-badge">Running</span>}
      </div>

      <div className="topology-workbench__chat-timeline" ref={timelineRef}>
        {hiddenHistoryCount > 0 && (
          <div className="topology-workbench__chat-history">
            <span className="topology-workbench__chat-history-text">
              {showPreviousHistory
                ? 'Showing previous workbench history.'
                : 'Previous workbench history is hidden for a clean start.'}
            </span>
            <button
              type="button"
              className="button button--ghost button--small"
              onClick={() => setShowPreviousHistory((current) => !current)}
            >
              {showPreviousHistory
                ? 'Hide previous history'
                : `Show previous history (${hiddenHistoryCount} message${hiddenHistoryCount === 1 ? '' : 's'})`}
            </button>
          </div>
        )}
        {messagesLoading ? (
          <div className="topology-workbench__chat-empty">Loading messages…</div>
        ) : messagesError ? (
          <div className="topology-workbench__chat-empty topology-workbench__chat-empty--error">{messagesError}</div>
        ) : messageEvents.length === 0 ? (
          <div className="topology-workbench__chat-empty">
            {hiddenHistoryCount > 0
              ? 'Ask about the current topology surface to start a new workbench change-set.'
              : 'Ask about the current topology surface, or attach readable files such as logs, configs, JSON, YAML, CSV, or TSV.'}
          </div>
        ) : (
          messageEvents.map((event) => {
            const role = event.role === 'assistant' ? 'assistant' : 'user';
            return (
              <article key={event.id} className={`topology-workbench__chat-message topology-workbench__chat-message--${role}`}>
                <div className="topology-workbench__chat-message-role">{role === 'assistant' ? 'Assistant' : 'You'}</div>
                <div className="topology-workbench__chat-message-text">
                  <ChatMessageContentBlocks
                    event={event}
                    answeredPromptKeys={promptAnswerState.answeredPromptKeys}
                    supersededPromptKeys={promptAnswerState.supersededPromptKeys}
                    latestResponsesByPromptKey={promptAnswerState.latestResponsesByPromptKey}
                    promptChoiceDrafts={promptChoiceDrafts}
                    promptInputDrafts={promptInputDrafts}
                    promptFormDrafts={promptFormDrafts}
                    promptSubmissionState={promptSubmissionState}
                    setPromptChoiceDrafts={setPromptChoiceDrafts}
                    setPromptInputDrafts={setPromptInputDrafts}
                    setPromptFormDrafts={setPromptFormDrafts}
                    submitSingleChoicePrompt={submitSingleChoicePrompt}
                    submitChoicePromptDraft={submitChoicePromptDraft}
                    submitConfirmationPrompt={submitConfirmationPrompt}
                    submitInputPrompt={submitInputPrompt}
                    submitFormPrompt={submitFormPrompt}
                  />
                </div>
                {(event.attachmentArtifacts ?? []).length > 0 && (
                  <ul className="topology-workbench__chat-attachments">
                    {(event.attachmentArtifacts ?? []).map((artifact) => {
                      const sizeLabel = formatArtifactSize(artifact.size);
                      const subtitle = [artifact.mimeType, sizeLabel].filter(Boolean).join(' · ');
                      return (
                        <li key={`${artifact.kind}-${artifact.id}`}>
                          <button
                            type="button"
                            className={`topology-workbench__chat-attachment-button${selectedArtifact?.id === artifact.id ? ' topology-workbench__chat-attachment-button--active' : ''}`}
                            onClick={() => void handleSelectArtifact(artifact)}
                            title={subtitle || artifact.summary || artifact.id}
                          >
                            <span>{artifact.summary ?? artifact.id}</span>
                            {subtitle ? <span className="topology-workbench__chat-attachment-meta">{subtitle}</span> : null}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </article>
            );
          })
        )}
        {runStatusText && <div className="topology-workbench__chat-status">{runStatusText}</div>}
        {runActivityTrail.length > 0 && (
          <div className="topology-workbench__chat-activity" aria-label="Investigation activity">
            {runActivityTrail
              .slice()
              .reverse()
              .map((entry) => (
                <div key={entry.id} className={`topology-workbench__chat-activity-item topology-workbench__chat-activity-item--${entry.eventType}`}>
                  {entry.summary}
                </div>
              ))}
          </div>
        )}
      </div>

      {selectedArtifact && (
        <div className="topology-workbench__chat-preview">
          <div className="topology-workbench__chat-preview-header">
            <div>
              <div className="topology-workbench__chat-preview-title">{selectedArtifact.summary ?? selectedArtifact.id}</div>
              <div className="topology-workbench__chat-preview-meta">
                {[selectedArtifact.mimeType, formatArtifactSize(selectedArtifact.size)].filter(Boolean).join(' · ')}
              </div>
            </div>
            <div className="topology-workbench__chat-preview-actions">
              {!selectedArtifactIsFile && !selectedArtifactPreview?.isFull ? (
                <button
                  type="button"
                  className="button button--ghost button--small"
                  onClick={() => void loadArtifactPreview(selectedArtifact, true)}
                  disabled={selectedArtifactPreview?.loading}
                >
                  Load full
                </button>
              ) : null}
              {!selectedArtifactIsFile ? (
                <button type="button" className="button button--ghost button--small" onClick={() => void handleOpenArtifactInNewTab(selectedArtifact)}>
                  Open raw
                </button>
              ) : null}
              {renderArtifactDownloadAction(
                selectedArtifact,
                'button button--ghost button--small',
                'chat-export-format__select chat-export-format__select--compact'
              )}
              <button type="button" className="button button--ghost button--small" onClick={() => setSelectedArtifact(null)}>
                Close
              </button>
            </div>
          </div>
          {selectedArtifactPreview?.loading ? <div className="topology-workbench__chat-status">Loading artifact…</div> : null}
          {selectedArtifactPreview?.error ? <div className="topology-workbench__chat-empty topology-workbench__chat-empty--error">{selectedArtifactPreview.error}</div> : null}
          {!selectedArtifactPreview?.loading && !selectedArtifactPreview?.error && selectedArtifactPreview
            ? renderArtifactPreview(
                selectedArtifact,
                selectedArtifactPreview,
                () => void loadArtifactPreview(selectedArtifact, true),
                (artifact) => void handleDownloadFile(artifact)
              )
            : null}
        </div>
      )}

      {pendingFiles.length > 0 && (
        <div className="topology-workbench__chat-files">
          {pendingFiles.map((item) => (
            <div key={item.localId} className="topology-workbench__chat-file">
              <div className="topology-workbench__chat-file-meta">
                <span>
                  {item.source === 'promoted'
                    ? (item.fileRecord?.filename ??
                      item.fileRecord?.name ??
                      (typeof item.uploadedAttachment?.resourceId === 'string' ? item.uploadedAttachment.resourceId : 'Promoted file'))
                    : item.file?.name ?? 'Local file'}
                </span>
                <span>
                  {Math.max(
                    1,
                    Math.round(
                      ((item.source === 'promoted' ? item.fileRecord?.sizeBytes : item.file?.size) ?? 0) / 1024
                    )
                  )}{' '}
                  KB
                </span>
                {item.status === 'uploading' && <span>Uploading…</span>}
                {item.source === 'promoted' && item.status !== 'uploading' && <span>Promoted file</span>}
              </div>
              {item.status !== 'uploading' && (
                <button type="button" className="button button--ghost button--small" onClick={() => removePendingFile(item.localId)}>
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="topology-workbench__chat-composer">
        <textarea
          ref={composerRef}
          className="textarea topology-workbench__chat-input"
          rows={5}
          value={composerText}
          onChange={(event) => setComposerText(event.target.value)}
          onKeyDown={handleComposerKeyDown}
          placeholder={disabled ? disabledReason ?? 'Resolve a topology view before chatting.' : 'Ask about the current topology surface…'}
          disabled={disabled || sending}
        />
        {composerError && <div className="topology-workbench__chat-error">{composerError}</div>}
        {disabled && disabledReason && <div className="topology-workbench__chat-hint">{disabledReason}</div>}
        {disabled && disabledActions ? (
          <div className="topology-workbench__chat-disabled-actions">{disabledActions}</div>
        ) : null}
        <div className="topology-workbench__chat-actions">
          <label className="topology-workbench__trace-level">
            <span>Detail</span>
            <select value={traceLevel} onChange={(event) => setTraceLevel(event.target.value as RunTraceLevel)} disabled={disabled || sending}>
              {RUN_TRACE_LEVEL_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <ComposerAttachmentPicker
            disabled={disabled || sending}
            requestOrgApi={requestOrgApi}
            onSelectLocalFiles={handleSelectFiles}
            onSelectPromotedFile={handleAttachPromotedFile}
            triggerClassName="button button--secondary"
            triggerAriaLabel="Attach file or promoted file"
            triggerContent="Attach files"
          />
          <button type="button" className="button" onClick={() => void handleSendMessage()} disabled={!canSend}>
            Send
          </button>
        </div>
      </div>
    </section>
  );
};

export default TopologyWorkbenchChatPanel;
